import type { TranslationKey } from '@finance/contracts';
import { status } from './semantic.js';

/**
 * Status presentation registry.
 *
 * Finance workflows and platform operations share one canonical vocabulary.
 * Each known status maps to a semantic tone, chip color, and compiler-known translation key.
 */
export type FinanceStatus =
  | 'draft'
  | 'submitted'
  | 'pending'
  | 'approved'
  | 'posted'
  | 'rejected'
  | 'withdrawn'
  | 'reversed'
  | 'failed'
  | 'completed'
  | 'planned'
  | 'disabled'
  | 'degraded'
  | 'stale'
  | 'open'
  | 'closed'
  | 'partial'
  | 'success'
  | 'enabled'
  | 'investigating'
  | 'resolved'
  | 'adjusted'
  | 'ignored'
  | 'elected'
  | 'allocated'
  | 'expired'
  | 'active'
  | 'inactive'
  | 'suspended'
  | 'ok'
  | 'unknown'
  | 'unhealthy'
  | 'cancelled'
  | 'canceled'
  | 'critical'
  | 'warning'
  | 'info';

export interface StatusPresentation {
  tone: string;
  color: 'default' | 'primary' | 'success' | 'info' | 'warning' | 'error';
  labelKey: TranslationKey;
}

export const STATUS_PRESENTATION: Readonly<Record<FinanceStatus, StatusPresentation>> = {
  draft: { tone: status.draft.tone, color: 'default', labelKey: 'status.draft' },
  submitted: { tone: status.submitted.tone, color: 'info', labelKey: 'status.submitted' },
  pending: { tone: status.pending.tone, color: 'warning', labelKey: 'status.pending' },
  approved: { tone: status.approved.tone, color: 'success', labelKey: 'status.approved' },
  posted: { tone: status.posted.tone, color: 'success', labelKey: 'status.posted' },
  rejected: { tone: status.rejected.tone, color: 'error', labelKey: 'status.rejected' },
  withdrawn: { tone: status.withdrawn.tone, color: 'default', labelKey: 'status.withdrawn' },
  reversed: { tone: status.reversed.tone, color: 'warning', labelKey: 'status.reversed' },
  failed: { tone: status.failed.tone, color: 'error', labelKey: 'status.failed' },
  completed: { tone: status.completed.tone, color: 'success', labelKey: 'status.completed' },
  planned: { tone: status.planned.tone, color: 'info', labelKey: 'status.planned' },
  disabled: { tone: status.disabled.tone, color: 'default', labelKey: 'status.disabled' },
  degraded: { tone: status.degraded.tone, color: 'warning', labelKey: 'status.degraded' },
  stale: { tone: status.stale.tone, color: 'default', labelKey: 'status.stale' },
  open: { tone: status.open.tone, color: 'error', labelKey: 'status.open' },
  closed: { tone: status.closed.tone, color: 'default', labelKey: 'status.closed' },
  partial: { tone: status.partial.tone, color: 'warning', labelKey: 'status.partial' },
  success: { tone: status.success.tone, color: 'success', labelKey: 'status.success' },
  enabled: { tone: status.enabled.tone, color: 'success', labelKey: 'status.enabled' },
  investigating: {
    tone: status.investigating.tone,
    color: 'info',
    labelKey: 'status.investigating',
  },
  resolved: { tone: status.resolved.tone, color: 'success', labelKey: 'status.resolved' },
  adjusted: { tone: status.adjusted.tone, color: 'warning', labelKey: 'status.adjusted' },
  ignored: { tone: status.ignored.tone, color: 'default', labelKey: 'status.ignored' },
  elected: { tone: status.submitted.tone, color: 'primary', labelKey: 'status.elected' },
  allocated: { tone: status.completed.tone, color: 'success', labelKey: 'status.allocated' },
  expired: { tone: status.disabled.tone, color: 'default', labelKey: 'status.expired' },
  active: { tone: status.enabled.tone, color: 'success', labelKey: 'status.active' },
  inactive: { tone: status.disabled.tone, color: 'default', labelKey: 'status.inactive' },
  suspended: { tone: status.pending.tone, color: 'warning', labelKey: 'status.suspended' },
  ok: { tone: status.success.tone, color: 'success', labelKey: 'status.ok' },
  unknown: { tone: status.stale.tone, color: 'default', labelKey: 'status.unknown' },
  unhealthy: { tone: status.failed.tone, color: 'error', labelKey: 'status.unhealthy' },
  cancelled: { tone: status.rejected.tone, color: 'error', labelKey: 'status.cancelled' },
  canceled: { tone: status.rejected.tone, color: 'error', labelKey: 'status.cancelled' },
  critical: { tone: status.failed.tone, color: 'error', labelKey: 'status.critical' },
  warning: { tone: status.pending.tone, color: 'warning', labelKey: 'status.warning' },
  info: { tone: status.submitted.tone, color: 'info', labelKey: 'status.info' },
} as const;

export function normalizeFinanceStatus(statusValue: string): FinanceStatus | undefined {
  const normalized = statusValue.toLowerCase();
  return normalized in STATUS_PRESENTATION ? (normalized as FinanceStatus) : undefined;
}

export function isFinanceStatus(statusValue: string): statusValue is FinanceStatus {
  return normalizeFinanceStatus(statusValue) !== undefined;
}

export function getStatusPresentation(statusValue: string): StatusPresentation {
  const normalized = normalizeFinanceStatus(statusValue);
  return normalized ? STATUS_PRESENTATION[normalized] : STATUS_PRESENTATION.unknown;
}
